// ─── Gym inbox: voice notes, videos, form checks and replies (member side) ───
// Pieces used by OrgInbox's ThreadView. Limits and labels come from
// utils/messageMedia.js (mirrors the API); the upload itself is utils/mediaUpload.js.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { V } from '../utils/theme';
import { Icons } from './Icons';
import { Btn } from './ui';
import { FullSheet } from './FullSheet';
import { onAccent } from './kit';
import {
  RECORD_MAX_SEC, EXERCISE_MAX, CAPTION_MAX, baseType, pickRecorderType, fmtDuration, fmtBytes,
  mediaSpec, mediaErrorText, mediaCaption, replyPreview, fileMediaType,
} from '../utils/messageMedia';

// ─── Voice recorder (tap to start, tap to stop, capped at the audio limit) ───
export const recorderSupported = () =>
  typeof window !== 'undefined' && typeof window.MediaRecorder === 'function' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

export function useVoiceRecorder() {
  const [state, setState] = useState('idle'); // idle | starting | recording | recorded
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null); // { blob, type, duration, url }
  const [error, setError] = useState(null);
  const rec = useRef(null), stream = useRef(null), chunks = useRef([]), t0 = useRef(0), timer = useRef(null), cancelled = useRef(false);

  const release = useCallback(() => {
    clearInterval(timer.current); timer.current = null;
    if (stream.current) { stream.current.getTracks().forEach(t => { try { t.stop(); } catch {} }); stream.current = null; }
  }, []);
  const stop = useCallback(() => { const r = rec.current; if (r && r.state !== 'inactive') { try { r.stop(); } catch {} } }, []);
  const cancel = useCallback(() => { cancelled.current = true; stop(); if (!rec.current) { release(); setState('idle'); } }, [stop, release]);
  const clear = useCallback(() => {
    setResult(r => { if (r && r.url) { try { URL.revokeObjectURL(r.url); } catch {} } return null; });
    setState('idle'); setElapsed(0);
  }, []);

  const start = useCallback(async () => {
    if (!recorderSupported()) { setError("Voice notes aren't supported in this browser."); return; }
    setError(null); setState('starting'); cancelled.current = false;
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = st;
      const mime = pickRecorderType((t) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(t));
      const r = new window.MediaRecorder(st, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : { audioBitsPerSecond: 64000 });
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        const duration = Math.min(RECORD_MAX_SEC, (Date.now() - t0.current) / 1000);
        const type = baseType(r.mimeType || mime || (chunks.current[0] && chunks.current[0].type) || '');
        release(); rec.current = null;
        if (cancelled.current || !chunks.current.length) { setState('idle'); setElapsed(0); return; }
        const blob = new Blob(chunks.current, { type });
        setResult({ blob, type, duration, url: URL.createObjectURL(blob) });
        setState('recorded');
      };
      rec.current = r;
      r.start(1000);
      t0.current = Date.now(); setElapsed(0); setState('recording');
      timer.current = setInterval(() => {
        const e = (Date.now() - t0.current) / 1000;
        setElapsed(e);
        if (e >= RECORD_MAX_SEC) stop();
      }, 250);
    } catch (e) {
      release(); rec.current = null; setState('idle');
      setError(e && e.name === 'NotAllowedError' ? 'Microphone access is blocked. Allow it in your browser settings to record.' : "Couldn't start recording.");
    }
  }, [release, stop]);

  // Leaving the thread mid-recording drops the recording and frees the microphone.
  useEffect(() => () => { cancelled.current = true; stop(); release(); }, [stop, release]);

  return { state, elapsed, result, error, start, stop, cancel, clear, setError };
}

// ─── Reading a picked video ───
// Duration from the file's metadata; null when the browser can't read it (the API treats
// duration as optional, and still checks the size).
export function readVideoDuration(file, timeoutMs = 5000) {
  return new Promise((resolve) => {
    let url = null, done = false;
    const finish = (v) => { if (done) return; done = true; if (url) { try { URL.revokeObjectURL(url); } catch {} } resolve(v); };
    try {
      url = URL.createObjectURL(file);
      const el = document.createElement('video');
      el.preload = 'metadata'; el.muted = true;
      el.onloadedmetadata = () => finish(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
      el.onerror = () => finish(null);
      el.src = url;
      setTimeout(() => finish(null), timeoutMs);
    } catch { finish(null); }
  });
}

// A picked file checked against the API limits → { spec, type, duration } or { error }
export async function checkVideoFile(file, purpose = 'message') {
  const type = fileMediaType(file);
  const duration = await readVideoDuration(file);
  const r = mediaSpec({ content_type: type, bytes: file.size, duration_sec: duration, purpose });
  if (r.error) return { error: mediaErrorText(r.error, r.detail) };
  if (r.spec.kind !== 'video') return { error: mediaErrorText('unsupported_media_type') };
  return { spec: r.spec, type, duration };
}

// ─── Rendering ───

// The quoted message above a reply
export function ReplyQuote({ reply, me, otherName, mine, onJump }) {
  const p = replyPreview(reply, me, otherName);
  const Tag = reply && onJump ? 'button' : 'div';
  return (
    <Tag {...(Tag === 'button' ? { type: 'button', onClick: () => onJump(reply.id), 'aria-label': `Replying to ${p.who}: ${p.text}. Show message` } : {})}
      data-testid="reply-quote"
      style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 6, paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8, borderRadius: 8,
        borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: `3px solid ${mine ? onAccent() : V.accent}`,
        background: mine ? 'rgba(0,0,0,0.12)' : V.inputBg, cursor: Tag === 'button' ? 'pointer' : 'default', fontFamily: V.font }}>
      {p.who && <div style={{ fontSize: 11, fontWeight: 700, color: mine ? onAccent() : V.accent }}>{p.who}</div>}
      <div style={{ fontSize: 12, color: mine ? onAccent() : V.text2, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: reply ? 'normal' : 'italic' }}>{p.text}</div>
    </Tag>
  );
}

// Audio / video player for a media message, with upload state for one still sending.
export function MediaContent({ m, mine, onMediaError, onRetry, onDiscard }) {
  const media = m.media || {};
  const up = m.upload || null;
  const fg = mine ? onAccent() : V.text;
  const sub = mine ? onAccent() : V.text3;
  const caption = mediaCaption(m);
  const header = media.purpose === 'form_check'
    ? `Form check${media.exercise ? ` · ${media.exercise}` : ''}`
    : media.kind === 'audio' ? 'Voice message' : 'Video';
  const onError = () => { if (!up && onMediaError) onMediaError(m); };
  return (
    <div data-testid="media-message" data-kind={media.kind} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: media.kind === 'video' ? 0 : 220 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: sub }}>
        {media.kind === 'audio' ? Icons.mic({ size: 13, color: sub }) : Icons.video({ size: 13, color: sub })}
        <span>{header}</span>
        {media.duration_sec ? <span style={{ fontFamily: V.mono, fontWeight: 600 }} data-testid="media-duration">· {fmtDuration(media.duration_sec)}</span> : null}
      </div>
      {media._broken && media.url ? (
        <div style={{ fontSize: 12, color: sub }}>
          Can't play this here. <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ color: mine ? onAccent() : V.accent, fontWeight: 700 }}>Open the file</a>
        </div>
      ) : !media.url ? (
        <div style={{ fontSize: 12, color: sub, fontStyle: 'italic' }}>{up ? 'Preparing…' : 'This recording is not available.'}</div>
      ) : media.kind === 'audio' ? (
        <audio controls preload="metadata" src={media.url} onError={onError} aria-label={`${header}${media.duration_sec ? `, ${fmtDuration(media.duration_sec)}` : ''}`}
          style={{ width: '100%', minWidth: 220, height: 40 }} />
      ) : (
        <video controls playsInline preload="metadata" src={media.url} onError={onError} aria-label={header}
          style={{ width: '100%', maxWidth: 320, maxHeight: 360, borderRadius: 10, background: '#000', display: 'block' }} />
      )}
      {caption && <div style={{ fontSize: 14, color: fg, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{caption}</div>}
      {up && (
        <div data-testid="media-upload" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {up.error ? (
            <>
              <div role="alert" style={{ fontSize: 12, fontWeight: 600, color: mine ? onAccent() : V.danger }}>{up.error}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => onRetry(m)} style={pillBtn(mine)}>Retry</button>
                <button type="button" onClick={() => onDiscard(m)} style={pillBtn(mine, true)}>Discard</button>
              </div>
            </>
          ) : (
            <>
              <div role="progressbar" aria-label="Upload progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((up.progress || 0) * 100)}
                style={{ height: 4, borderRadius: 2, background: mine ? 'rgba(0,0,0,0.18)' : V.hoverBg, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round((up.progress || 0) * 100)}%`, background: mine ? onAccent() : V.accent, transition: 'width .2s' }} />
              </div>
              <div style={{ fontSize: 11, color: sub, fontFamily: V.mono }}>
                {up.stage === 'send' || up.stage === 'done' ? 'Sending…' : up.stage === 'begin' ? 'Preparing…' : `Uploading ${Math.round((up.progress || 0) * 100)}%`}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
const pillBtn = (mine, quiet) => ({
  paddingTop: 5, paddingBottom: 5, paddingLeft: 12, paddingRight: 12, borderRadius: 14, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: V.font,
  background: quiet ? 'transparent' : (mine ? 'rgba(0,0,0,0.2)' : `${V.accent}18`), color: mine ? onAccent() : (quiet ? V.text3 : V.accent),
  border: `1px solid ${mine ? 'rgba(0,0,0,0.25)' : V.cardBorder}`,
});

// ─── Form check sheet: exercise (catalog or typed) + a video + an optional note ───
export function FormCheckSheet({ exerciseNames = [], onClose, onSend }) {
  const [exercise, setExercise] = useState('');
  const [picked, setPicked] = useState(null); // { file, type, duration, url }
  const [note, setNote] = useState('');
  const [err, setErr] = useState(null);
  const [checking, setChecking] = useState(false);
  const recordRef = useRef(null), chooseRef = useRef(null), headRef = useRef(null);
  useEffect(() => { headRef.current?.focus(); }, []);
  useEffect(() => () => { if (picked && picked.url) { try { URL.revokeObjectURL(picked.url); } catch {} } }, [picked]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setChecking(true); setErr(null);
    const r = await checkVideoFile(file, 'form_check');
    setChecking(false);
    if (r.error) { setErr(r.error); return; }
    setPicked({ file, type: r.type, duration: r.duration, url: URL.createObjectURL(file) });
  };
  const send = () => {
    if (!picked) { setErr('Record or choose a video first.'); return; }
    onSend({ file: picked.file, type: picked.type, duration: picked.duration, exercise: exercise.trim().slice(0, EXERCISE_MAX), caption: note.trim().slice(0, CAPTION_MAX) });
  };
  const names = [...new Set(exerciseNames.filter(Boolean))].sort((a, b) => a.localeCompare(b));

  return (
    <FullSheet title="Form check" ariaLabel="Send a form check" onClose={onClose} closeLabel="Cancel" zIndex={9991}
      footer={<div>
        {err && <div role="alert" style={{ fontSize: 13, color: V.danger, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>{err}</div>}
        <Btn full onClick={send} disabled={!picked || checking}>Send to coach</Btn>
      </div>}>
      <h2 ref={headRef} tabIndex={-1} style={{ fontSize: 20, fontWeight: 800, color: V.text, margin: 0, outline: 'none' }}>Send a form check</h2>
      <p style={{ fontSize: 13, color: V.text2, lineHeight: 1.5, marginTop: 6 }}>Film a set from the side and your coach will review your technique. Up to 5 minutes and 50 MB.</p>
      <label htmlFor="fc-exercise" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: V.text, marginTop: 16, marginBottom: 8 }}>Exercise</label>
      <input id="fc-exercise" list="fc-exercise-list" value={exercise} maxLength={EXERCISE_MAX} autoComplete="off" placeholder="e.g. Back squat"
        onChange={e => setExercise(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', minHeight: 44, paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderRadius: 12,
          background: V.inputBg, border: `1px solid ${V.cardBorder}`, color: V.text, fontSize: 16, fontFamily: V.font, outline: 'none' }} />
      <datalist id="fc-exercise-list">{names.map(n => <option key={n} value={n} />)}</datalist>

      <div style={{ fontSize: 14, fontWeight: 600, color: V.text, marginTop: 18, marginBottom: 8 }} id="fc-video-label">Video</div>
      <div role="group" aria-labelledby="fc-video-label" style={{ display: 'flex', gap: 8 }}>
        <Btn v="secondary" onClick={() => recordRef.current?.click()} s={{ flex: 1 }}>{Icons.video({ size: 15, color: V.text2 })} Record</Btn>
        <Btn v="secondary" onClick={() => chooseRef.current?.click()} s={{ flex: 1 }}>{Icons.upload({ size: 15, color: V.text2 })} Choose</Btn>
      </div>
      <input ref={recordRef} type="file" accept="video/*" capture="environment" onChange={onFile} style={{ display: 'none' }} data-testid="fc-record-input" />
      <input ref={chooseRef} type="file" accept="video/*" onChange={onFile} style={{ display: 'none' }} data-testid="fc-choose-input" aria-label="Choose a video" />
      {checking && <div role="status" style={{ fontSize: 12, color: V.text3, marginTop: 8 }}>Checking the video…</div>}
      {picked && (
        <div style={{ marginTop: 10 }}>
          <video src={picked.url} controls playsInline preload="metadata" style={{ width: '100%', maxHeight: 280, borderRadius: 12, background: '#000' }} aria-label="Selected video" />
          <div data-testid="fc-picked" style={{ fontSize: 12, color: V.text3, marginTop: 4 }}>
            {picked.file.name || 'Video'} · {fmtBytes(picked.file.size)}{picked.duration ? ` · ${fmtDuration(picked.duration)}` : ''}
          </div>
        </div>
      )}

      <label htmlFor="fc-note" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: V.text, marginTop: 18, marginBottom: 8 }}>Note <span style={{ fontWeight: 400, color: V.text3 }}>(optional)</span></label>
      <textarea id="fc-note" value={note} maxLength={CAPTION_MAX} rows={3} placeholder="Anything your coach should look at?" onChange={e => setNote(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderRadius: 12, resize: 'vertical',
          background: V.inputBg, border: `1px solid ${V.cardBorder}`, color: V.text, fontSize: 16, fontFamily: V.font, outline: 'none', lineHeight: 1.45 }} />
    </FullSheet>
  );
}
